import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, shallowEqual } from "react-redux";
import AnalyticsBreadcrumb from "./AnalyticsBreadcrumb";
import { usePageTranslations } from "../../hooks/usePageTranslations";
import { NoraIcon, RoiIcon, ThematicIcon } from "../CopilotV2/Home";
import { 
  feedbackIcon, 
  analyticsIcon, 
  layers, 
  heatmapIcon, 
  dataScienceIcon, 
  outcomesIcon, 
  sharingIcon,
  aiIcon,
  groupIcon,
  dashboardIconNew,
  reportIconNew,
  questionsIcon,
  questionAnalyticsIcon,
  benchmarkIcon,
  correlationsIcon,
} from "../../layouts/LeaderLayout/Icons";
import {
  filterAnalyticsOptionsForEmployee,
  extractEmployeeAuthDetails,
  parseAnalyticsPermissionsFromDetails,
} from "../../utilities/analyticsPermissions";

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const [loadingCard, setLoadingCard] = useState(null);
  const t = usePageTranslations('ProtoType/NewAnalytics/AnalyticsDashboard/translations');

  // Get custom data from Redux to check if it was loaded through NewAnalytics
  const { raw_data } = useSelector(
    (state) => ({
      raw_data: state.audit?.raw_data,
    }),
    shallowEqual
  );

   // Permission-based visibility: hide Feedback Insights without read access
  const { employee, permissionRecords, employeeId } = useSelector(
    (state) => ({
      employee: state.employees?.userEmp,
      permissionRecords: state.employee_permission?.employee_permission,
      employeeId: state.auth?.employee_id,
    }),
    shallowEqual
  );

  // Read presence of analytics schema from core_data or surveyquestions as fallback
  const { coreAnalytics, surveyAnalytics } = useSelector(
    (state) => ({
      coreAnalytics: state.audit?.core_data?.questions,
      surveyAnalytics:
        state.surveyquestions?.[0]?.questions?.analytics ||
        state.surveyquestions?.survey_questions?.[0]?.questions?.analytics,
    }),
    shallowEqual
  );


  // Check if custom data is available and redirect if not
  useEffect(() => {
    const hasCustomData = raw_data && 
                         Array.isArray(raw_data) && 
                         raw_data.length > 0 && 
                         raw_data[0]?.datasetMetadata;
    
    // If no custom data is found, redirect back to NewAnalytics index
    if (!hasCustomData) {
      console.log("No custom data found, redirecting to NewAnalytics index");
      navigate('/leader/audit/new-analytics', { replace: true });
      return;
    }

  }, [raw_data, navigate, datasetId]);

  // Don't render anything if we don't have custom data (will redirect)
  const hasCustomData = raw_data && 
                       Array.isArray(raw_data) && 
                       raw_data.length > 0 && 
                       raw_data[0]?.datasetMetadata;

  if (!hasCustomData) {
    return null; // Component will redirect in useEffect
  }

  const palette = [
    { fg: "#2D70E2", bg: "#D5E2F9" },
    { fg: "#27CDA7", bg: "#D4F5ED" },
    { fg: "#F15062", bg: "#FCDCE0" },
    { fg: "#FEA711", bg: "#FFEDCF" },
  ];

  const aiOptions = [
    {
      key: "nora",
      title: t('noraAI'),
      description: t('noraAIDescription'),
      Icon: NoraIcon,
      route: "/leader/audit/copilot?w=nora",
      fg: "#2D70E2",
      bg: "#D5E2F9",
    },
    {
      key: "thematic",
      title: t('thematicAnalysis'),
      description: t('thematicAnalysisDescription'),
      Icon: ThematicIcon,
      route: "/leader/audit/copilot?w=thematic",
      fg: "#FEA711",
      bg: "#FFEDCF",
    },
    {
      key: "roi",
      title: t('roiAnalysis'),
      description: t('roiAnalysisDescription'),
      Icon: RoiIcon,
      route: "/leader/audit/copilot?w=roi",
      fg: "#27CDA7",
      bg: "#D4F5ED",
    },
  ];

  const surveyOptions = [
    {
      key: "dashboard",
      title: t('dashboard'),
      description: t('dashboardDescription'),
      iconType: "dashboard",
      route: "/leader/audit/simple/dashboard",
    },
    {
      key: "question-analytics",
      title: t('questionRanking'),
      description: t('questionRankingDescription'),
      iconType: "question-analytics",
      route: "/leader/audit/simple/question-analytics",
    },
    {
      key: "heatmap",
      title: t('heatmapping'),
      description: t('heatmappingDescription'),
      iconType: "heatmap",
      route: "/leader/audit/simple/heatmap",
      fg: "#FEA711",
      bg: "#FFEDCF",
    },
    {
      key: "question",
      title: t('questionDistributions'),
      description: t('questionDistributionsDescription'),
      iconType: "questions",
      route: "/leader/audit/simple/question",
    },
    {
      key: "factor",
      title: t('categoryAnalytics'),
      description: t('categoryAnalyticsDescription'),
      iconType: "report",
      route: "/leader/audit/simple/factor",
    },
    {
      key: "benchmarks",
      title: t('benchmarking'),
      description: t('benchmarkingDescription'),
      iconType: "benchmark",
      route: "/leader/audit/simple/benchmarks",
      fg: "#B745A6",
      bg: "#F1DAED",
    },
    {
      key: "feedback",
      title: t('comments'),
      description: t('commentsDescription'),
      iconType: "feedback",
      route: "/leader/audit/simple/feedback",
      fg: "#84CC16",
      bg: "#E6F5D0",
    },
    {
      key: "correlations",
      title: t('categoryCorrelations'),
      description: t('categoryCorrelationsDescription'),
      iconType: "correlations",
      route: "/leader/audit/simple/correlations",
      fg: "#22C5F6",
      bg: "#D3F3FD",
    },
    {
      key: "completion_timeline",
      title: t('completionTimeline'),
      description: t('completionTimelineDescription'),
      iconType: "analytics",
      route: "/leader/audit/simple/completion-timeline",
      fg: "#2D70E2",
      bg: "#D5E2F9",
    },
  ];

  // Conditionally include Custom Analytics card if analytics schema is present
  // and the dataset param is identified (not just present as 'unidentified')
  const datasetParam = (datasetId || '').toLowerCase();
  const isValidDataset = !!datasetParam && datasetParam !== 'unidentified';
  const showCustomAnalytics = isValidDataset && !!(coreAnalytics || surveyAnalytics);
  if (showCustomAnalytics) {
    surveyOptions.push({
      key: "custom_analytics",
      title: t('customAnalytics'),
      description: t('customAnalyticsDescription'),
      iconType: "quadrant",
      route: "/leader/audit/simple/custom-analytics",
      fg: "#7F56D9",
      bg: "#E5DDF7",
    });
  }

  const renderIcon = (iconType, color) => {
    const c = color || "#64748b";
    switch(iconType) {
      case "dashboard": return dashboardIconNew(c);
      case "datascience": return dataScienceIcon(c);
      case "factor": return FactorSlidersIcon(c);
      case "layers": return layers(c);
      case "report": return reportIconNew(c);
      case "analytics": return analyticsIcon(c);
      case "outcomes": return outcomesIcon(c);
      case "sharing": return sharingIcon(c);
      case "ai": return aiIcon(c);
      case "group": return groupIcon(c);
      case "benchmark": return benchmarkIcon(c);
      case "quadrant": return QuadrantIcon(c);
      case "question-analytics": return questionAnalyticsIcon(c);
      case "questions": return questionsIcon(c);
      case "heatmap": return heatmapIcon(c);
      case "feedback": return feedbackIcon(c);
      case "correlations": return correlationsIcon(c);
      default: return "📊";
    }
  };

  const buildRouteWithDataset = (route) => {
    if (!datasetId) return route;
    const [path, search = ""] = route.split("?");
    const params = new URLSearchParams(search);
    params.set("dataset", datasetId);
    const queryString = params.toString();
    return `${path}?${queryString}`;
  };

  const handleCardClick = async (option) => {
    setLoadingCard(option.key);

    // Simulate loading delay for smooth UX
    await new Promise(resolve => setTimeout(resolve, 300));

    // Add dataset ID to the route if it exists
    const finalRoute = buildRouteWithDataset(option.route);
    navigate(finalRoute);
  };

  const isAdmin = employee?.account_type === 5;
  const canReadFeedback = isAdmin || employee?.read_feedback === 1;
  const visibleSurveyOptions = surveyOptions.filter(
    (opt) => opt.key !== 'feedback' || canReadFeedback
  );
  // Keep feedback (comments) in sync with the parent feedback toggle:
  // if feedback access is off, we must also hide the comments analytics card.
  const filteredSurveyOptions = filterAnalyticsOptionsForEmployee(
    visibleSurveyOptions,
    {
      employee,
      permissionRecords,
      employeeId,
    }
  ).filter((option) => option.key !== 'feedback' || canReadFeedback);

  // Filter AI options based on permissions
  // Nora is checked separately (nora_ai/noraAI), thematic and roi are in ANALYTICS_CARD_PERMISSIONS
  const authDetails = extractEmployeeAuthDetails(permissionRecords, employeeId);

  // Check if any permission records exist (for backward compatibility)
  const hasPermissionRecords = Array.isArray(permissionRecords) &&
    permissionRecords.length > 0 &&
    Array.isArray(authDetails) &&
    authDetails.length > 0;

  // Check for Nora access with backward compatibility
  // Nora is independent - if permission doesn't exist, default to enabled
  // If permission exists and is explicitly 'false', deny access
  const hasNoraAccess = isAdmin || (() => {
    if (!hasPermissionRecords) return true; // Backward compatibility: default to enabled
    const noraDetail = (authDetails || []).find(
      (d) =>
        d?.value === 'nora_ai' ||
        d?.name === 'nora_ai' ||
        d?.name_id === 'nora_ai' ||
        d?.value === 'noraAI' ||
        d?.name === 'noraAI' ||
        d?.name_id === 'noraAI'
    );
    // If nora permission doesn't exist, default to enabled (backward compatibility)
    if (!noraDetail) return true;
    // If explicitly set to false, deny access
    if (noraDetail?.response === false || noraDetail?.value === 'false') return false;
    // Otherwise, grant access
    return true;
  })();

  // Get parsed analytics permissions for thematic and roi
  // parseAnalyticsPermissionsFromDetails returns null if nothing found, or object with keys
  const parsedAnalytics = (() => {
    if (isAdmin) return null; // Admins see everything
    if (employee?.access_data_analytics !== 1) return null;
    return parseAnalyticsPermissionsFromDetails(authDetails);
  })();

  const filteredAiOptions = aiOptions.filter((option) => {
    if (isAdmin) return true;

    // Check Nora separately - independent of other permissions with backward compatibility
    if (option.key === 'nora') {
      return hasNoraAccess;
    }

    // Check thematic and roi from analytics permissions - work like other analytics cards
    // No backward compatibility - must be explicitly granted
    if (option.key === 'thematic' || option.key === 'roi') {
      if (employee?.access_data_analytics !== 1) return false;
      // Use parsedAnalytics - if null or key not found, deny access
      if (!parsedAnalytics) return false;
      // Only show if explicitly set to true
      return parsedAnalytics[option.key] === true;
    }

    // Default: show if we have analytics access
    return employee?.access_data_analytics === 1;
  });

  return (
    <Container>
      <AnalyticsBreadcrumb />

      {filteredAiOptions.length > 0 && (
        <Section>
          <SectionTitle>{t('aiToolkit')}</SectionTitle>
          <CardsGrid>
            {filteredAiOptions.map((option, idx) => {
            const scheme = {
              fg: option.fg || palette[idx % palette.length].fg,
              bg: option.bg || palette[idx % palette.length].bg,
            };
            const CardComponent = option.featured ? FeaturedCard : AnalyticsCard;
            const IconComponent = option.Icon;
            const iconVariant = 'ai';
            const gradient = { from: '#27CDA7', to: '#2D70E2' };
            const gradientId = `ai-gradient-${option.key}`;
            return (
              <CardComponent
                key={option.key}
                onClick={() => handleCardClick(option)}
                isLoading={loadingCard === option.key}
                featured={option.featured}
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <CardTop>
                  <IconBox
                    variant={iconVariant}
                    featured={option.featured}
                    featuredBg={scheme.bg}
                    bg={scheme.bg}
                  >
                    <IconWrapper variant={iconVariant}>
                      {IconComponent ? (
                        <IconComponent
                          color={gradient ? undefined : scheme.fg}
                          fill={gradient ? undefined : scheme.fg}
                          gradient={gradient}
                          gradientId={gradientId}
                        />
                      ) : (
                        renderIcon(option.iconType, scheme.fg)
                      )}
                    </IconWrapper>
                  </IconBox>
                  <CardTitle featured={option.featured}>{option.title}</CardTitle>
                </CardTop>

                <CardBody>
                  <CardDescription>{option.description}</CardDescription>
                </CardBody>

                <CardFooter>
                  {loadingCard === option.key ? (
                    <LoadingSpinner />
                  ) : (
                    <OpenButton
                      onClick={(e) => { e.stopPropagation(); handleCardClick(option); }}
                        aria-label={t('openCard').replace('{title}', option.title)}
                    >
                        <span>{t('open')}</span>
                      <ExploreArrow />
                    </OpenButton>
                  )}
                </CardFooter>
              </CardComponent>
            );
          })}
          </CardsGrid>
        </Section>
      )}

      <Section>
        <SectionTitle>{t('dataVisualizations')}</SectionTitle>
        <CardsGrid>
          {filteredSurveyOptions.map((option, idx) => {
            const scheme = {
              fg: option.fg || palette[idx % palette.length].fg,
              bg: option.bg || palette[idx % palette.length].bg,
            };
            const CardComponent = option.featured ? FeaturedCard : AnalyticsCard;
            const IconComponent = option.Icon;
            const iconVariant = 'default';
            const gradient = null;
            const gradientId = undefined;
            return (
              <CardComponent
                key={option.key}
                onClick={() => handleCardClick(option)}
                isLoading={loadingCard === option.key}
                featured={option.featured}
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <CardTop>
                  <IconBox
                    variant={iconVariant}
                    featured={option.featured}
                    featuredBg={scheme.bg}
                    bg={scheme.bg}
                  >
                    <IconWrapper variant={iconVariant}>
                      {IconComponent ? (
                        <IconComponent
                          color={gradient ? undefined : scheme.fg}
                          fill={gradient ? undefined : scheme.fg}
                          gradient={gradient}
                          gradientId={gradientId}
                        />
                      ) : (
                        renderIcon(option.iconType, scheme.fg)
                      )}
                    </IconWrapper>
                  </IconBox>
                  <CardTitle featured={option.featured}>{option.title}</CardTitle>
                </CardTop>

                <CardBody>
                  <CardDescription>{option.description}</CardDescription>
                </CardBody>

                <CardFooter>
                  {loadingCard === option.key ? (
                    <LoadingSpinner />
                  ) : (
                    <OpenButton
                      onClick={(e) => { e.stopPropagation(); handleCardClick(option); }}
                        aria-label={t('openCard').replace('{title}', option.title)}
                    >
                        <span>{t('open')}</span>
                      <ExploreArrow />
                    </OpenButton>
                  )}
                </CardFooter>
              </CardComponent>
            );
          })}
        </CardsGrid>
      </Section>
    </Container>
  );
};

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const cascadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px) scale(0.98); filter: blur(2px); }
  60% { opacity: 1; transform: translateY(0) scale(1.01); filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// Styled Components
const Container = styled.div`
  position: relative;
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-height: 100vh;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 140%;
    transform: translateX(-50%);
    z-index: -1;
    pointer-events: none;
    background:
      radial-gradient(1200px 500px at 10% -10%, rgba(45, 112, 226, 0.12), transparent 60%),
      radial-gradient(1100px 460px at 110% 10%, rgba(39, 205, 167, 0.16), transparent 55%),
      radial-gradient(900px 380px at 50% -10%, rgba(255, 255, 255, 0.55), transparent 70%);
    opacity: 0.35;
    filter: saturate(100%);
  }
`;

const Section = styled.section`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 27px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
  letter-spacing: -0.01em;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  animation: ${fadeIn} 1s ease-out 0.2s both;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const AnalyticsCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  transition: box-shadow 0.25s ease, transform 0.2s ease, border-color 0.25s ease;
  display: flex;
  flex-direction: column;
  padding: 16px;
  animation: ${cascadeIn} 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
  will-change: transform, opacity;
  opacity: 0;
  min-height: 190px;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(148, 163, 184, 0.32);
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
  }

  ${props => props.isLoading && `
    pointer-events: none;
    opacity: 0.8;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const FeaturedCard = styled(AnalyticsCard)`
  position: relative;
  overflow: hidden;
  border-color: rgba(45, 112, 226, 0.28); /* Blue 500 tint */
  box-shadow: 0 8px 24px rgba(45, 112, 226, 0.18);
  min-height: 190px;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(280px 140px at 10% -20%, rgba(254, 167, 17, 0.22), transparent 60%),   /* Orange 500 */
      radial-gradient(240px 120px at 85% -20%, rgba(39, 205, 167, 0.18), transparent 60%),  /* Teal 500 */
      radial-gradient(260px 120px at 50% -10%, rgba(45, 112, 226, 0.20), transparent 60%);   /* Blue 500 */
    opacity: 1;
    z-index: 0;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: #d1d5db;
    box-shadow: 0 10px 24px rgba(0,0,0,0.06);
  }

  > * { position: relative; z-index: 1; }
`;


// New card layout helpers
const CardTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const IconBox = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: ${({ variant, featured, featuredBg, bg }) => {
    if (variant === 'ai') {
      return `linear-gradient(135deg, rgba(39, 205, 167, 0.12) 0%, rgba(45, 112, 226, 0.28) 100%)`;
    }
    return featured ? (featuredBg || '#D5E2F9') : (bg || '#f8fafc');
  }};
  border: ${({ variant, featured }) => {
    if (variant === 'ai') {
      return '1px solid rgba(255, 255, 255, 0.45)';
    }
    return featured ? 'rgba(255,255,255,0.45)' : '#eef2f7';
  }};
  box-shadow: ${({ variant }) =>
    variant === 'ai' ? '0 10px 26px rgba(45, 112, 226, 0.18)' : 'none'};
  transition: all 0.25s ease;
`;

const CardBody = styled.div`
  margin-top: 10px;
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  text-align: left;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
  font-weight: 400;
  text-align: left;
`;

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 20px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`;

const OpenButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: color 0.18s ease, transform 0.18s ease;

  &:hover {
    color: #0f172a;
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
    border-radius: 6px;
  }
`;

const ExploreArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M8 16L16 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M9 8h7v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LoadingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #94a3b8;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-right: 4px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ variant }) => (variant === 'ai' ? 1 : 0.9)};
  transform: ${({ variant }) => (variant === 'ai' ? 'translateY(0)' : 'none')};
  
  svg {
    width: 22px;
    height: 22px;
    display: block;
    filter: ${({ variant }) => (variant === 'ai' ? 'drop-shadow(0 6px 10px rgba(45, 112, 226, 0.15))' : 'none')};
  }
`;

 

export default AnalyticsDashboard;

// Local minimal icons for consistency
function FactorSlidersIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g stroke="#1f2937" strokeWidth="1.6" strokeLinecap="round">
        <path d="M4 6h10"/>
        <path d="M4 12h16"/>
        <path d="M4 18h8"/>
        <circle cx="16" cy="6" r="2.2" stroke="#94a3b8"/>
        <circle cx="10" cy="12" r="2.2" stroke="#94a3b8"/>
        <circle cx="14" cy="18" r="2.2" stroke="#94a3b8"/>
      </g>
    </svg>
  );
}

function BenchmarkMedalIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="8.5" r="4.5" stroke="#1f2937" strokeWidth="1.6"/>
      <path d="M8 13l-1.5 7L12 17l5.5 3L16 13" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function QuadrantIcon(fill) {
  const stroke = fill || '#1f2937';
  const soft = '#94a3b8';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="18" height="18" rx="3.5" stroke={stroke} strokeWidth="1.6"/>
      <path d="M12 3v18M3 12h18" stroke={soft} strokeWidth="1.6" strokeLinecap="round"/>
      <circle cx="16.5" cy="7.5" r="1.6" fill={stroke} opacity="0.85"/>
      <circle cx="7.5" cy="16.5" r="1.6" fill={stroke} opacity="0.85"/>
    </svg>
  );
}
